import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _dec, _class, _AbBaseDropdown, _class2, _AbDropdownContent;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbListBox } from "abreact/incubator/ui/component/AbListBox";
import { BaseRCmp } from "abreact/incubator/ui/component/BaseRCmp";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbIcon } from "abreact/misc/AbIcon";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { action, makeObservable } from "mobx";
import { observer } from "mobx-react";
import * as PropTypes from "prop-types";
import * as React from "react";
import * as ReactDOM from "react-dom";

/**
 * The base properties that can be specified when constructing a dropdown.
 */

/**
 * A React-based implementation of a dropdown component.
 */
export let AbBaseDropdown = (_dec = action.bound, _class = (_AbBaseDropdown = class AbBaseDropdown extends BaseRCmp {
  constructor(props) {
    super(props);
    _defineProperty(this, "dropdownContent", void 0);
    _defineProperty(this, "dropdownElement", null);
    _defineProperty(this, "popupHandle", null);
    _defineProperty(this, "registerDropdownElement", dropdownElement => {
      this.dropdownElement = dropdownElement;
    });
    makeObservable(this);
    this.installDropdownMachine(this.props);
    this.updateDropdownMachineFromProps(props);
  }
  componentDidUpdate() {
    if (this.props.dropdownMachine && this.props.dropdownMachine !== this.dropdownMachine) {
      this.installDropdownMachine(this.props);
    }
    this.updateDropdownMachineFromProps(this.props);
  }
  componentWillUnmount() {
    super.componentWillUnmount();

    // This hides the dropdown's popup when the dropdown itself unmounts
    this.hidePopup();
  }
  get dropdownMachine() {
    return this._dropdownMachine;
  }

  /**
   * Returns the primary class name for the dropdown.
   */

  get itemRenderer() {
    return this.props.itemRenderer !== undefined ? this.props.itemRenderer : AbListBox.defaultItemRenderer.bind(this);
  }
  get valueRenderer() {
    return this.props.valueRenderer !== undefined ? this.props.valueRenderer : AbListBox.defaultItemRenderer.bind(this);
  }

  /**
   * Creates a default dropdown machine, based upon the props provided.
   *
   * Note: the default dropdown machine is only created if the container doesn't pass
   * a machine through its props.
   */

  /**
   * Installs a new dropdown machine for use by the dropdown.
   *
   * If a dropdown machine is provided via props, it will be installed. If not,
   * then a default dropdown machine will be created based upon the other props.
   */
  installDropdownMachine(props) {
    this._dropdownMachine = props.dropdownMachine || this.createDefaultDropdownMachine(props);
  }

  /**
   * Updates the installed dropdown machine based upon any changes to the props.
   */
  updateDropdownMachineFromProps(props) {
    // TODO Make these defaults defined in one place, logic is scattered throughout multiple classes
    this._dropdownMachine.selectionMode = props.selectionMode || SelectionMode.SINGLE;
    this._dropdownMachine.autoAccept = props.autoAccept || false;
    this._dropdownMachine.popupPosition = props.popupPosition || PopupPosition.BELOW;
  }
  canShowPopup() {
    return this.dropdownContent !== undefined || this.dropdownMachine.items.length > 0;
  }
  showPopup() {
    if (this.canShowPopup()) {
      this.dropdownMachine.onShowPopup();
      const popup = this.renderPopup();
      const id = this.props.popupProperties ? this.props.popupProperties.id : undefined;
      this.popupHandle = PopupUtils.showPopupExplicitly(popup, {
        id: id
      });
    }
    return this.popupHandle || undefined;
  }
  hidePopup() {
    if (this.popupHandle !== null) {
      this.popupHandle.close();
      this.popupHandle = null;
    }
  }

  /**
   * Invoked when an item is clicked on.
   */
  onItemClick(item) {
    this.onItemChosen(item);
  }

  /**
   * Invoked when an item is chosen.
   */
  onItemChosen(item) {
    this.dropdownMachine.onItemChosen(item);
  }

  /**
   * Invoked when the enter key is pressed.
   */
  onEnter() {
    if (this.dropdownMachine.showingPopup) {
      this.hidePopup();
      const selectedItems = this.dropdownMachine.popupSelectedItems;
      if (selectedItems.length === 1) {
        this.onItemChosen(selectedItems[0]);
      }
    }
  }
  getDefaultPopupPositioner(props) {
    return this.props.popupPositioner ?? (PopupUtils.defaultPositioner(this.props.popupPosition) || PopupUtils.VERTICAL_POSITIONER);
  }
  popupPositioner(props) {
    const popupPositioner = this.getDefaultPopupPositioner(props);
    const positionInfo = popupPositioner(props);
    this.dropdownMachine.popupPosition = positionInfo.popupPosition;
    return positionInfo;
  }
  onBackgroundCaptureMouseDown(event) {
    // Only close the popup if the click is in the background, not on the dropdown itself
    const target = event.target;
    const thisElement = ReactDOM.findDOMNode(this);
    if (!thisElement.contains(target)) {
      this.hidePopup();
    }
  }
  renderPopup() {
    const classes = ReactUtils.classNamesString(["ab-dropdown-popup", this.props.popupProperties && this.props.popupProperties.className]);

    // TS bug spreading generics so the cast is needed
    let popupProperties = {
      ...this.props.popupProperties,
      className: classes
    };
    return /*#__PURE__*/React.createElement(AbPopup, _extends({}, popupProperties, {
      flushContent: true,
      position: this.props.popupPosition,
      positioner: this.popupPositioner,
      origin: this.dropdownElement || undefined,
      onKeyDown: event => this.onKeyDown(event),
      overlay: false,
      onBackgroundCaptureMouseDown: event => this.onBackgroundCaptureMouseDown(event),
      dataTestId: this.props.dataTestId
    }), /*#__PURE__*/React.createElement(AbDropdownContent, {
      dropdownMachine: this.dropdownMachine
    }, this.dropdownContent || this.renderPopupItems(this.dropdownMachine.items)));
  }
  renderPopupItems(items) {
    return /*#__PURE__*/React.createElement(AbListBox, {
      listMachine: this.dropdownMachine.listMachine,
      items: items,
      itemDisplayStringFunction: this.dropdownMachine.itemDisplayStringFunction,
      itemRenderer: this.props.itemRenderer,
      onItemClick: item => this.onItemClick(item)
    });
  }
  onKeyDown(event) {
    if (this.dropdownMachine) {
      const popupAcceptsFocus = Boolean(this.props.popupProperties && this.props.popupProperties.focus);
      if (event.key === KeyValue.ESCAPE) {
        if (!this.props.autoAccept) {
          this.dropdownMachine.clear();
        }
        this.hidePopup();
        return true;
      } else if (event.key === KeyValue.TAB && !popupAcceptsFocus) {
        this.hidePopup();
        if (!this.props.autoAccept) {
          this.dropdownMachine.clear();
        }
        // Note: don't prevent default so that the user can still tab out of the dropdown
        return false;
      } else if (event.key === KeyValue.ENTER) {
        // Chose the currently selected item or items as the value
        this.onEnter();
        return true;
      } else if (event.key === " " && this.dropdownMachine.showingPopup && this.dropdownMachine.popupSelectedItems.length > 0 && !popupAcceptsFocus) {
        this.onItemChosen(this.dropdownMachine.selectedItem);
        return true;
      } else if (event.key === KeyValue.ARROW_UP) {
        if (this.dropdownMachine.showingPopup) {
          if (this.props.autoAccept) {
            this.dropdownMachine.selectPreviousItem();
          } else {
            this.dropdownMachine.selectPreviousItemOrClear();
          }
        } else {
          this.showPopup();
        }
        return true;
      } else if (event.key === KeyValue.ARROW_DOWN) {
        if (this.dropdownMachine.showingPopup) {
          this.dropdownMachine.selectNextItem();
        } else {
          this.showPopup();
        }
        return true;
      }
    }
    return false;
  }
  onKeyPress(event) {
    if (this.dropdownMachine.showingPopup) {
      if (this.dropdownMachine.listMachine.onKeyPress(event.key)) {
        event.preventDefault();
      }
    }
  }
}, _defineProperty(_AbBaseDropdown, "defaultProps", {
  disabled: false,
  selectionMode: SelectionMode.SINGLE,
  popupPosition: PopupPosition.BELOW,
  autoAccept: false
}), _AbBaseDropdown), _applyDecoratedDescriptor(_class.prototype, "popupPositioner", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "popupPositioner"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onKeyDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onKeyDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onKeyPress", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onKeyPress"), _class.prototype), _class);

/**
 * The properties of the dropdown contents.
 */
let AbDropdownContent = observer(_class2 = (_AbDropdownContent = class AbDropdownContent extends BaseRCmp {
  getChildContext() {
    return {
      selectionManager: this.props.dropdownMachine.listMachine.selectionManager
    };
  }
  componentDidMount() {
    this.props.dropdownMachine.onShowPopup();
  }
  componentWillUnmount() {
    super.componentWillUnmount();
    this.props.dropdownMachine.onHidePopup();
  }
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-dropdown-content",
      "aria-live": "polite"
    }, this.props.children);
  }
}, _defineProperty(_AbDropdownContent, "childContextTypes", {
  selectionManager: PropTypes.any
}), _AbDropdownContent)) || _class2;
const positionToIconString = popupPosition => {
  switch (popupPosition) {
    case PopupPosition.ABOVE:
      return "absdk:chevron-up";
    case PopupPosition.LEFT:
      return "absdk:chevron-left";
    case PopupPosition.RIGHT:
      return "absdk:chevron-right";
    default:
      return "absdk:chevron-down";
    // down, undefined, fixed, etc.
  }
};

/**
 * A simple icon component that shows an arrow pointing to the dropdown's popup.
 */
export const AbDropdownIcon = props => {
  return /*#__PURE__*/React.createElement(AbIcon, {
    image: positionToIconString(props.popupPosition),
    className: "ab-dropdown-icon",
    altText: ""
  });
};
//# sourceMappingURL=AbBaseDropdown.js.map
